import api from './api'
import { identityHint } from './utils/sessionCookie'
import { getRetailerSuccess } from './actions/retailer'
import history from './history'
import { getStoreRef } from './storeRef'

// Seeded from the identity hint cookie so the first paint after a reload already
// knows whether anyone is signed in. This used to be read out of localStorage,
// alongside the JWT; the token is now in an HttpOnly cookie and only the
// non-secret half is readable, which is all this needs.
let loggedIn = !!identityHint()

// The app renders nothing until init() settles, so the session check at startup
// must not be allowed to hang: an axios timeout rejects with no status, which
// init() already treats as "keep the signed-in state from the cookie".
const SESSION_CHECK_TIMEOUT_MS = 10000

/**
 * The HTTP status of a failed request, whichever shape the error arrived in.
 *
 * `request.*` goes through utils/jsonapi, which normalises to `error.status`.
 * `api.session()` and `api.logout()` call httpClient directly and reject with a
 * raw axios error, where it is `error.response.status`. Reading only one of the
 * two silently treated every session failure as "no status", which is how a
 * transient error became a logout.
 */
const statusOf = error =>
  (error && error.response && error.response.status) ||
  (error && error.status) ||
  null

/**
 * The signed-in user, in the shape the app expects.
 *
 * Nothing is persisted here any more — the cookie *is* the store. The previous
 * saveCurrentUser() wrote identity to localStorage next to the token; the server
 * now issues the same fields as `gemsby_user` on every login and refresh, so
 * keeping a second copy would only add a way for the two to disagree.
 */
function currentUserFromCookie() {
  return identityHint()
}

export default {
  init() {
    api.setErrorHandler(this.handleTokenExpired.bind(this))

    const hint = identityHint()
    if (!hint) {
      loggedIn = false
      return Promise.resolve()
    }

    // Confirms the cookie is still a live session (and rotates it) rather than
    // trusting the hint, which is readable and therefore not a credential.
    return api
      .session({ timeout: SESSION_CHECK_TIMEOUT_MS })
      .catch(error => {
        // 401 is the only answer that means "this session is dead". Anything
        // else — a 5xx, a dropped connection, a route not deployed yet — says
        // nothing about whether the cookie is still good, and treating it as a
        // logout is what turns a blip into being thrown back to the login page
        // right after signing in. Carry on with the hint; if the session really
        // is gone, the page's own requests will 401 and handleTokenExpired
        // deals with it.
        if (statusOf(error) === 401) {
          throw error
        }
        // eslint-disable-next-line no-console
        console.warn(
          'Session check did not complete; keeping the signed-in state from the cookie.',
          error
        )
      })
      .then(() => {
        // Admin users don't load a retailer profile on init — they manage all
        // retailers and the API may not return a profile for their account.
        if (hint.retailerType !== 'admin') {
          return api
            .getRetailer(hint.id)
            .then(retailer => {
              getStoreRef().dispatch(getRetailerSuccess(retailer))
              return hint
            })
            .catch(error => {
              // 401 means session is invalid; any other error (404, 500, network)
              // still keeps the user logged in — we just won't have their retailer
              // profile pre-loaded (components will fetch it on demand).
              if (statusOf(error) === 401) {
                return undefined
              }
              return hint
            })
        }
        return hint
      })
      .then(
        currentUser => {
          loggedIn = !!currentUser
          return currentUser
        },
        () => {
          // Only reached for a real 401 now — the cookie is not a live session.
          loggedIn = false
        }
      )
  },
  loggedIn() {
    return loggedIn
  },
  currentUser() {
    return currentUserFromCookie()
  },
  /**
   * Rotates the session cookie. Nothing is returned to the caller because there
   * is nothing for it to hold on to — the refreshed token went into the cookie.
   */
  refreshSession() {
    return api.session().then(() => {
      loggedIn = true
    })
  },
  login(email, password) {
    return api.login(email, password).then(login => {
      loggedIn = true
      // The response sets gemsby_session (HttpOnly) plus the readable
      // gemsby_user hint, so nothing is stored here. It still carries a `token`
      // attribute while AUTH_EXPOSE_TOKEN_IN_BODY is on for the apps that have
      // not migrated; this one deliberately ignores it.
      //
      // The user entity out of `included[]` is still what gets handed back,
      // rather than the cookie hint: it carries the full profile (verified,
      // publicLink, …) that reducers and the navigation read straight after
      // login, and the hint is a deliberately minimal identity.
      return { currentUser: login.getRelated('current_user') || login }
    })
  },
  logout() {
    loggedIn = false
    // The cookies can only be cleared by the server that set them, and this is
    // also what finally invalidates the token — the old localStorage logout
    // just dropped the app's copy and left the JWT valid until it expired.
    return api.logout().then(
      () => undefined,
      error => {
        // A failed logout must still leave the app signed out locally; the
        // cookie may survive until it expires, which is the best available
        // outcome when the API is unreachable.
        console.warn('Logout request failed:', error)
      }
    )
  },
  register(data) {
    data.username = data.email
    data.verifyPassword = data.password
    return api.addRetailer({
      type: 'retailer',
      id: '',
      attributes: data
    })
  },
  handleTokenExpired(fn) {
    return (...args) => {
      return fn(...args).catch(error => {
        if (statusOf(error) === 401 && identityHint()) {
          return this.refreshSession().then(
            () => {
              return fn(...args)
            },
            () => {
              this.logout()
              history.replace('/login')
            }
          )
        }
        throw error
      })
    }
  }
}
